import React, { useState, useEffect } from "react";
import Button from "../components/Button";
import useInView from "../hooks/useInView";
import useContactHeadline from "../hooks/useContactHeadline";

const EMAILJS_SERVICE_ID = "service_20hdcyz";
const EMAILJS_TEMPLATE_ID = "template_m7i9wb6";
const EMAILJS_PUBLIC_KEY = "q6a_FM_gEyz3iq-V_";

export default function Contact() {
  const [heroRef, heroInView] = useInView();
  const [formRef, formInView] = useInView();
  const { headline, loading } = useContactHeadline();
  const [isMobile, setIsMobile] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [honeypot, setHoneypot] = useState("");
  const [formStatus, setFormStatus] = useState("");
  const [lastSubmit, setLastSubmit] = useState(0);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (honeypot) return;

    const now = Date.now();
    if (now - lastSubmit < 60000) {
      setFormStatus("error");
      return;
    }

    setFormStatus("sending");
    setLastSubmit(now);

    window.emailjs
      .send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        { name: formData.name, email: formData.email, message: formData.message },
        EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setFormStatus("success");
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setFormStatus(""), 3000);
      })
      .catch(() => {
        setFormStatus("error");
      });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const lineStyle = {
    display: "block",
    width: "100%",
    border: "none",
    borderBottom: "1px solid #52525b",
    borderRadius: 0,
    background: "transparent",
    color: "#fff",
    fontSize: "18px",
    fontFamily: "Inter, sans-serif",
    padding: "8px 0",
    outline: "none",
    WebkitAppearance: "none",
  };

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    color: "#71717a",
    marginBottom: "8px",
    fontFamily: "Inter, sans-serif",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  };

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Hero — unchanged */}
      <section
        ref={heroRef}
        style={{ padding: isMobile ? "160px 15px 60px" : "240px 48px 120px" }}
      >
        <div style={{ maxWidth: "768px", margin: "0 auto", textAlign: "center" }}>
          <h1
            style={{
              fontFamily: "Archivo, sans-serif",
              fontWeight: 900,
              letterSpacing: "-0.02em",
              fontVariationSettings: '"wght" 600, "wdth" 75',
              fontSize: "clamp(40px, 6vw + 16px, 90px)",
              lineHeight: 0.95,
              marginBottom: isMobile ? "16px" : "32px",
              textTransform: "uppercase",
              minHeight: "90px",
              whiteSpace: "pre-line",
            }}
          >
            <span
              style={{
                display: "block",
                opacity: heroInView ? 1 : 0,
                filter: `blur(${heroInView ? 0 : 10}px)`,
                transform: `translateY(${heroInView ? 0 : -20}px)`,
                transition: "opacity 0.8s ease-out, transform 0.8s ease-out, filter 0.8s ease-out",
              }}
            >
              {loading ? "NEED SOMETHING?" : headline}
            </span>
          </h1>

          <p
            style={{
              fontSize: "20px",
              lineHeight: 1.6,
              color: "#9ca3af",
              marginBottom: isMobile ? "24px" : "48px",
              opacity: heroInView ? 1 : 0,
              filter: `blur(${heroInView ? 0 : 10}px)`,
              transform: `translateY(${heroInView ? 0 : -20}px)`,
              transition: "opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s, filter 0.8s ease-out 0.3s",
            }}
          >
            Email me. Or WhatsApp me.
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              justifyContent: "center",
              marginBottom: isMobile ? "24px" : "48px",
              opacity: heroInView ? 1 : 0,
              filter: `blur(${heroInView ? 0 : 10}px)`,
              transform: `translateY(${heroInView ? 0 : -20}px)`,
              transition: "opacity 0.8s ease-out 0.45s, transform 0.8s ease-out 0.45s, filter 0.8s ease-out 0.45s",
            }}
          >
            <Button href="mailto:mail@rene-gebhardt.com">EMAIL</Button>
            <Button href="https://wa.me/4917620191826" external>WHATSAPP</Button>
          </div>

          <div
            style={{
              padding: isMobile ? "16px" : "24px",
              backgroundColor: "#27272a",
              borderRadius: "2px",
              border: "1px solid #3f3f46",
              maxWidth: "512px",
              margin: "0 auto",
              opacity: heroInView ? 1 : 0,
              filter: `blur(${heroInView ? 0 : 10}px)`,
              transform: `translateY(${heroInView ? 0 : -20}px)`,
              transition: "opacity 0.8s ease-out 0.6s, transform 0.8s ease-out 0.6s, filter 0.8s ease-out 0.6s",
            }}
          >
            <p style={{ fontSize: "16px", lineHeight: 1, color: "#d4d4d8", marginBottom: "8px" }}>
              <strong style={{ color: "#ffffff" }}>I respond within 24 hours.</strong>
            </p>
            <p style={{ fontSize: "14px", lineHeight: 1, color: "#a1a1aa" }}>
              If I don't, send a reminder.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section
        ref={formRef}
        style={{ padding: isMobile ? "32px 15px 96px" : "64px 48px 128px" }}
      >
        <div style={{ maxWidth: "700px", margin: "0 auto" }}>

          <h2
            className="heading-section"
            style={{
              textAlign: "center",
              marginBottom: "75px",
              fontSize: "clamp(30px, 3.3vw + 10px, 60px)",
              opacity: formInView ? 1 : 0,
              transform: `translateY(${formInView ? 0 : -20}px)`,
              transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
            }}
          >
            THE OLD-FASHIONED WAY.
          </h2>

          <form onSubmit={handleSubmit}>

            {/* Name */}
            <div style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }} aria-hidden="true">
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>

            {/* Surname */}
            <div style={{ marginBottom: "48px", marginTop: "6px", opacity: formInView ? 1 : 0, transition: "opacity 0.8s ease-out 0.1s" }}>
              <label htmlFor="name" style={labelStyle}>My name is</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                style={lineStyle}
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: "48px", marginTop: "6px", opacity: formInView ? 1 : 0, transition: "opacity 0.8s ease-out 0.2s" }}>
              <label htmlFor="email" style={labelStyle}>You can reach me at</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={lineStyle}
              />
            </div>

            {/* Message — 4 decorative lines, textarea overlaid */}
            <div style={{ marginBottom: "48px", opacity: formInView ? 1 : 0, transition: "opacity 0.8s ease-out 0.3s" }}>
              <label htmlFor="message" style={labelStyle}>We should talk about</label>
              <div style={{ position: "relative", height: "216px" }}>
                {[0, 1, 2, 3].map(i => (
                  <div key={i} aria-hidden="true" style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: `${i * 54}px`,
                    height: "54px",
                    borderBottom: "1px solid #52525b",
                    pointerEvents: "none",
                  }} />
                ))}
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "216px",
                    border: "none",
                    borderRadius: 0,
                    background: "transparent",
                    color: "#fff",
                    fontSize: "18px",
                    fontFamily: "Inter, sans-serif",
                    lineHeight: "54px",
                    padding: "0",
                    outline: "none",
                    resize: "none",
                    overflow: "hidden",
                    WebkitAppearance: "none",
                    zIndex: 1,
                  }}
                />
              </div>
            </div>

            {/* Submit — centered, using global-btn-link style */}
            <div style={{
              display: "flex",
              justifyContent: "center",
              opacity: formInView ? 1 : 0,
              transition: "opacity 0.8s ease-out 0.4s",
            }}>
              <button
                type="submit"
                disabled={formStatus === "sending"}
                className="global-btn-link"
                style={{
                  fontFamily: "Inter, sans-serif",
                  textDecoration: "none",
                  color: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "18px",
                  background: "transparent",
                  border: "none",
                  cursor: formStatus === "sending" ? "default" : "pointer",
                  opacity: formStatus === "sending" ? 0.5 : 1,
                }}
              >
                <span className="global-btn-bracket-l" style={{ fontSize: "32px", fontWeight: "300", lineHeight: "1", position: "relative", top: "-1px" }}>[</span>
                <span className="global-btn-text-wrapper" style={{ display: "inline-block", overflow: "hidden", padding: "0 8px", whiteSpace: "nowrap" }}>
                  <span className="global-btn-text" style={{ fontSize: "17px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "3px", display: "inline-block" }}>
                    {formStatus === "sending" ? "SENDING..." : "SEND IT"}
                  </span>
                </span>
                <span className="global-btn-bracket-r" style={{ fontSize: "32px", fontWeight: "300", position: "relative", top: "-1px", lineHeight: "1" }}>]</span>
              </button>
            </div>

            {formStatus === "success" && (
              <p style={{ marginTop: "24px", fontSize: "16px", color: "#a1a1aa", textAlign: "center" }}>
                Got it. I'll get back to you.
              </p>
            )}

            {formStatus === "error" && (
              <p style={{ marginTop: "24px", fontSize: "16px", color: "#a1a1aa", textAlign: "center" }}>
                Something went wrong. Please try again.
              </p>
            )}

          </form>
        </div>
      </section>
    </div>
  );
}